import type { FinanceQuoteLineItem } from "~/types/finance";

import { calculateLineItemTotal, customerFacingUnitPrice } from "./lineItems";

export type QuoteBreakdownAmounts = {
  subtotal: number;
  discount: number;
  markup: number;
  tax: number;
  total: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Derive breakdown from line items — API does not expose these fields. */
export const deriveQuoteBreakdown = (
  lineItems: FinanceQuoteLineItem[] | null | undefined,
  taxPercent = 0,
  options?: { foldMarkupIntoPrice?: boolean },
): QuoteBreakdownAmounts => {
  let subtotal = 0;
  let discount = 0;
  let markup = 0;
  const foldMarkup = Boolean(options?.foldMarkupIntoPrice);

  for (const item of lineItems ?? []) {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    const discountPct = Number(item.discountPercent) || 0;
    const markupPct = Number(item.markupPercent) || 0;

    if (foldMarkup) {
      // Public / PDF: markup in Price; discount % of marked-up unit.
      const unit = customerFacingUnitPrice(item);
      const base = qty * unit;
      subtotal += base;
      discount += base * (discountPct / 100);
    } else {
      // OEM: markup first on base, then discount on after-markup.
      const base = qty * price;
      const markupAmt = base * (markupPct / 100);
      const afterMarkup = base + markupAmt;
      subtotal += base;
      markup += markupAmt;
      discount += afterMarkup * (discountPct / 100);
    }
  }

  const beforeTax = subtotal + markup - discount;
  const tax = beforeTax * (taxPercent / 100);
  const lineTotals = (lineItems ?? []).reduce(
    (sum, row) => sum + (row.total ?? calculateLineItemTotal(row)),
    0,
  );

  return {
    subtotal: round2(subtotal),
    discount: round2(discount),
    markup: round2(markup),
    tax: round2(tax),
    total: round2(lineTotals),
  };
};
